import React, { FC } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import TwGenericMenu, { TwGenericMenuOptions } from '../TwGenericMenu/TwGenericMenu';
import { SearchOtherExpenseResponse } from '../../redux/api/otherExpenses/otherExpense.model';
import DateUtils from '../../utils/dateUtils';
import './TwExpenseCard.scss';
import { ExpenseUtils } from '../../utils/expensesUtils';
import NumberUtils from '../../utils/numberUtils';
import { styled } from '@mui/material/styles';
import { ReactComponent as AttachmentIcon } from '../../assets/icons/clip.svg';

const TwBaseCard = styled(Card)(({}) => ({
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);',
  background: '#FFFFFF',
  borderRadius: '4px',
  overflow: 'initial'
}));

export interface TwExpenseCardProps {
  key: string;
  otherExpense: SearchOtherExpenseResponse;
  onOpenEditOtherExpenseDialog: (id: string) => void;
  onOpenViewOtherExpenseDialog: (id: string) => void;
  onOpenDeleteOtherExpenseDialog: (id: string, expenseName: string, equipmentName?: string) => void;
  isDriverLink?: boolean;
}

interface OtherExpenseMenuProps {
  expense: SearchOtherExpenseResponse;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
  onDelete: (id: string, expenseName: string, equipmentName?: string) => void;
}

const TwExpenseCard: FC<TwExpenseCardProps> = ({
  otherExpense,
  onOpenEditOtherExpenseDialog,
  onOpenDeleteOtherExpenseDialog,
  onOpenViewOtherExpenseDialog,
  isDriverLink = false
}): JSX.Element => {
  const OtherExpenseMenu: React.FC<OtherExpenseMenuProps> = ({
    expense: { id, name, equipment },
    onEdit,
    onView,
    onDelete
  }): JSX.Element => {
    const driverLinkOptions: TwGenericMenuOptions[] = [
      {
        label: 'View',
        action: (): void => {
          onView && onView(id);
        }
      }
    ];

    const options: TwGenericMenuOptions[] = [
      {
        label: 'Edit',
        action: (): void => {
          onEdit(id);
        }
      },
      {
        label: 'Delete',
        className: 'other_expense_table__delete',
        action: (): void => {
          onDelete(id, name, equipment?.name);
        }
      }
    ];
    return (
      <div className="other_expense_table__options">
        <TwGenericMenu options={isDriverLink ? driverLinkOptions : options} />
      </div>
    );
  };

  return (
    <TwBaseCard
      sx={{
        '& .MuiPaper-root-MuiCard-root': {
          overflow: 'initial'
        }
      }}
    >
      <CardContent
        className="expense-card__content"
        sx={{
          '& .MuiCardContent-root:last-child': {
            paddingBottom: '0px'
          }
        }}
      >
        <Box className="expense-card__content__menu">
          <OtherExpenseMenu
            expense={otherExpense}
            onEdit={onOpenEditOtherExpenseDialog}
            onDelete={onOpenDeleteOtherExpenseDialog}
            onView={onOpenViewOtherExpenseDialog}
          />
        </Box>

        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: '100%',
            height: '100%'
          }}
          spacing={1}
        >
          <Box className="expense-card__content__item">
            <Typography className="expense-card__content__item__title">Name</Typography>
            <Typography className="expense-card__content__item__value">
              {otherExpense.name}
            </Typography>
          </Box>
          <Box className="expense-card__content__item">
            <Typography className="expense-card__content__item__title">Date</Typography>
            <Typography className="expense-card__content__item__value">{`${DateUtils.getRangeMidiumDate(
              new Date(otherExpense.date_from),
              otherExpense.date_to ? new Date(otherExpense.date_to) : null
            )}`}</Typography>
          </Box>
          <Box className="expense-card__content__item">
            <Typography className="expense-card__content__item__title">Type</Typography>
            <Typography className="expense-card__content__item__value">{`${
              otherExpense.type ? ExpenseUtils.getTypeLabel(otherExpense.type) : ''
            }`}</Typography>
          </Box>
          <Box className="expense-card__content__item">
            <Typography className="expense-card__content__item__title">Driver</Typography>
            <Typography className="expense-card__content__item__value">
              {ExpenseUtils.getDriverName(otherExpense)}
            </Typography>
          </Box>
          <Box className="expense-card__content__item">
            <Typography className="expense-card__content__item__title">Classification</Typography>
            <Typography className="expense-card__content__item__value">{`${
              otherExpense.classification
                ? ExpenseUtils.getClassificationLabel(otherExpense.classification)
                : ''
            }`}</Typography>
          </Box>
          <Box className="expense-card__content__item">
            <Typography className="expense-card__content__item__title">Equipment</Typography>
            <Typography className="expense-card__content__item__value">
              {otherExpense.equipment?.name || '-'}
            </Typography>
          </Box>
          <Box className="expense-card__content__item">
            <Typography className="expense-card__content__item__title">Amount</Typography>
            <Typography className="expense-card__content__item__value">{`$${NumberUtils.formatNumber(
              otherExpense.amount,
              0
            )}`}</Typography>
          </Box>
        </Stack>

        {otherExpense.other_expense_attachments.length > 0 && (
          <Box className="expense-card__content__attachments">
            <Box className="expense-card__content__attachments__icon">
              <AttachmentIcon />
              {otherExpense.other_expense_attachments.length}
            </Box>
          </Box>
        )}
      </CardContent>
    </TwBaseCard>
  );
};

export default TwExpenseCard;
